import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ArrowUpDown,
  Eye,
  User,
  Star,
  List,
  Gauge,
  Edit,
  Mail,
  FileText,
  Download,
  Trash2,
  Plus,
  CheckSquare,
  X,
  MoreVertical,
  Calendar
} from "lucide-react";
import EditSubmissionStatusModal from "../components/EditSubmissionStatusModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CandidatePipeline = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [candidateStatusFilter, setCandidateStatusFilter] = useState("all");
  const [jobStatusFilter, setJobStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [showActionPanel, setShowActionPanel] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
    fetchAvailableStatuses();
  }, []);

  const fetchAvailableStatuses = async () => {
    try {
      const response = await axios.get(`${API}/submissions/statuses/available`);
      if (response.data.success) {
        setAvailableStatuses(response.data.all_statuses || []);
      }
    } catch (error) {
      console.error('Error fetching available statuses:', error);
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/submissions`);
      // Backend returns {submissions: [...], pagination: {...}, filters_applied: {...}}
      const data = response.data;
      setSubmissions(data.submissions && Array.isArray(data.submissions) ? data.submissions : []);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  // Selection management functions
  const handleSelectSubmission = (submissionId) => {
    if (selectedSubmissions.includes(submissionId)) {
      setSelectedSubmissions(selectedSubmissions.filter(id => id !== submissionId));
    } else {
      setSelectedSubmissions([...selectedSubmissions, submissionId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedSubmissions.length === filteredAndSortedSubmissions.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(filteredAndSortedSubmissions.map(sub => sub.id));
    }
  };

  const clearSelection = () => {
    setSelectedSubmissions([]);
    setShowActionPanel(false);
  };

  // Update action panel visibility based on selection
  useEffect(() => {
    setShowActionPanel(selectedSubmissions.length > 0);
  }, [selectedSubmissions]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.submission-action-dropdown') && !event.target.closest('button[title="More Actions"]')) {
        document.querySelectorAll('.submission-action-dropdown').forEach(dropdown => {
          dropdown.classList.add('hidden');
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleChangeStatus = (submission) => {
    setSelectedSubmission(submission);
    setShowStatusModal(true);
    // Close all dropdowns
    document.querySelectorAll('.submission-action-dropdown').forEach(dropdown => {
      dropdown.classList.add('hidden');
    });
  };

  const handleEditSubmission = (submission) => {
    setEditingSubmission(submission);
    setEditFormData({
      candidate_name: submission.candidate_name || '',
      candidate_email: submission.candidate_email || '',
      job_title: submission.job_title || '',
      recruiter_name: submission.recruiter_name || '',
      current_status: submission.current_status || '',
      submission_date: submission.submission_date || '',
      notes: submission.notes || ''
    });
    setShowEditModal(true);
    // Close all dropdowns
    document.querySelectorAll('.submission-action-dropdown').forEach(dropdown => {
      dropdown.classList.add('hidden');
    });
  };

  const updateSubmissionDetails = async () => {
    if (!editingSubmission) return;
    
    setUpdatingSubmission(true);
    try {
      const response = await axios.put(`${API}/submissions/${editingSubmission.id}`, {
        candidate_name: editFormData.candidate_name,
        candidate_email: editFormData.candidate_email,
        job_title: editFormData.job_title,
        recruiter_name: editFormData.recruiter_name,
        notes: editFormData.notes
      });
      
      if (response.data.success) {
        // Refresh submissions list
        await fetchSubmissions();
        setShowEditModal(false);
        setEditingSubmission(null);
        setEditFormData({});
        console.log('Submission updated successfully');
      }
    } catch (error) {
      console.error('Error updating submission:', error);
    } finally {
      setUpdatingSubmission(false);
    }
  };

  const updateSubmissionStatus = async (newStatus) => {
    if (!selectedSubmission || !newStatus) return;
    
    setUpdatingStatus(true);
    try {
      const response = await axios.post(`${API}/submissions/${selectedSubmission.id}/status`, {
        new_status: newStatus.value,
        change_reason: `Status changed to ${newStatus.label}`
      });
      
      if (response.data.success) {
        // Refresh submissions list
        await fetchSubmissions();
        setShowStatusModal(false);
        setSelectedSubmission(null);
        setSelectedNewStatus(null);
        // Show success message (you can add toast notification here)
        console.log(`Status updated to ${newStatus.label}`);
      }
    } catch (error) {
      console.error('Error updating submission status:', error);
      // Show error message (you can add toast notification here)
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort submissions
  const filteredSubmissions = (submissions || []).filter(submission => {
    const matchesSearch = submission.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.recruiter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         submission.id?.toString().includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || submission.current_status === statusFilter;
    const matchesCandidateStatus = candidateStatusFilter === 'all' || submission.candidate_status === candidateStatusFilter;
    const matchesJobStatus = jobStatusFilter === 'all' || submission.job_status === jobStatusFilter;
    
    return matchesSearch && matchesStatus && matchesCandidateStatus && matchesJobStatus;
  });

  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'submission_date' || sortField === 'updated_at') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubmissions = sortedSubmissions.slice(startIndex, startIndex + itemsPerPage);

  // Get unique values for filters
  const uniqueStatuses = [...new Set((submissions || []).map(s => s.current_status).filter(Boolean))];
  const uniqueCandidateStatuses = [...new Set((submissions || []).map(s => s.candidate_status).filter(Boolean))];
  const uniqueJobStatuses = [...new Set((submissions || []).map(s => s.job_status).filter(Boolean))];

  const getStatusColor = (status) => {
    const colors = {
      'sourced': 'bg-blue-100 text-blue-800',
      'screened': 'bg-purple-100 text-purple-800',
      'submitted': 'bg-yellow-100 text-yellow-800',
      'client_reviewing': 'bg-orange-100 text-orange-800',
      'client_accepted': 'bg-green-100 text-green-800',
      'client_rejected': 'bg-red-100 text-red-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_scheduled': 'bg-indigo-100 text-indigo-800',
      'interview_passed': 'bg-green-100 text-green-800',
      'interview_failed': 'bg-red-100 text-red-800',
      'offer_made': 'bg-emerald-100 text-emerald-800',
      'offer_accepted': 'bg-green-100 text-green-800',
      'offer_rejected': 'bg-red-100 text-red-800',
      'placed': 'bg-green-100 text-green-800',
      'started': 'bg-blue-100 text-blue-800',
      'rejected': 'bg-red-100 text-red-800',
      'withdrawn': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Use filteredAndSortedSubmissions for selection logic
  const filteredAndSortedSubmissions = sortedSubmissions;

  // Handler for successful status update
  const handleStatusUpdateSuccess = () => {
    setShowEditModal(false);
    setEditingSubmission(null);
    fetchSubmissions(); // Refresh the submissions list
  };

  // Status Change Modal Component
  const StatusChangeModal = () => {
    if (!showStatusModal || !selectedSubmission) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]">
        <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Change Submission Status</h3>
            <button
              onClick={() => {
                setShowStatusModal(false);
                setSelectedSubmission(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Candidate:</strong> {selectedSubmission.candidate_name}
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Job:</strong> {selectedSubmission.job_title}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              <strong>Current Status:</strong> 
              <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedSubmission.current_status)}`}>
                {selectedSubmission.current_status}
              </span>
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Status:
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableStatuses.map((status) => (
                <button
                  key={status.value}
                  onClick={() => {
                    if (status.value !== selectedSubmission.current_status && !updatingStatus) {
                      setSelectedNewStatus(status);
                    }
                  }}
                  disabled={updatingStatus || status.value === selectedSubmission.current_status}
                  className={`w-full text-left p-3 rounded-md border transition-all duration-200 ${
                    status.value === selectedSubmission.current_status
                      ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                      : selectedNewStatus?.value === status.value
                      ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200 shadow-sm'
                      : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  } ${updatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{status.label}</div>
                      <div className="text-xs text-gray-500">{status.description}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {status.value === selectedSubmission.current_status && (
                        <span className="text-xs text-gray-400">(Current)</span>
                      )}
                      {selectedNewStatus?.value === status.value && (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <CheckSquare className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowStatusModal(false);
                setSelectedSubmission(null);
                setSelectedNewStatus(null);
              }}
              disabled={updatingStatus}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (selectedNewStatus) {
                  updateSubmissionStatus(selectedNewStatus);
                }
              }}
              disabled={updatingStatus || !selectedNewStatus}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                updatingStatus || !selectedNewStatus
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500'
              }`}
            >
              {updatingStatus ? 'Updating...' : 'Update Status'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Action Panel Component
  const ActionPanel = () => {
    const selectedCount = selectedSubmissions.length;
    
    return (
      <div className="fixed right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg z-50">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {selectedCount} SELECTED
            </h3>
            <button
              onClick={clearSelection}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* UPDATE Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide mb-3">UPDATE</h4>
            <div className="space-y-2">
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Edit className="h-4 w-4 mr-3" />
                Change Status
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Edit className="h-4 w-4 mr-3" />
                Change Candidates Status
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-red-600 hover:bg-red-50 rounded-md">
                <Trash2 className="h-4 w-4 mr-3" />
                Mass Delete
              </button>
            </div>
          </div>

          {/* ACTIONS Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide mb-3">ACTIONS</h4>
            <div className="space-y-2">
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <FileText className="h-4 w-4 mr-3" />
                Add Note
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Plus className="h-4 w-4 mr-3" />
                Add Submission
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Star className="h-4 w-4 mr-3" />
                Add to Tearsheet
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <CheckSquare className="h-4 w-4 mr-3" />
                Create Task List
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Mail className="h-4 w-4 mr-3" />
                Email Contacts
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Mail className="h-4 w-4 mr-3" />
                Email Candidates
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Trash2 className="h-4 w-4 mr-3" />
                Delete Web Responses
              </button>
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <ArrowUpDown className="h-4 w-4 mr-3" />
                Move to Submission
              </button>
            </div>
          </div>

          {/* EXPORT AS Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 uppercase tracking-wide mb-3">EXPORT AS</h4>
            <div className="space-y-2">
              <button className="flex items-center w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md">
                <Download className="h-4 w-4 mr-3" />
                CSV
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Edit Submission Status Modal */}
      <EditSubmissionStatusModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingSubmission(null);
        }}
        submission={editingSubmission}
        onSaveSuccess={handleStatusUpdateSuccess}
      />
      
      {/* Status Change Modal */}
      <StatusChangeModal />
      
      {/* Action Panel - shows when submissions are selected */}
      {showActionPanel && <ActionPanel />}
      
      <div className={`space-y-6 transition-all duration-300 ${showActionPanel ? 'mr-80' : ''}`} data-testid="candidate-pipeline-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Submissions</h1>
        <p className="text-gray-600 mt-1">Track candidate submissions and progress</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>
                {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>

          <select
            value={candidateStatusFilter}
            onChange={(e) => setCandidateStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Candidate Status</option>
            {uniqueCandidateStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <select
            value={jobStatusFilter}
            onChange={(e) => setJobStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Job Status</option>
            {uniqueJobStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>

          <div className="flex items-center space-x-2">
            <List className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">{sortedSubmissions.length} submissions</span>
          </div>
        </div>
      </div>

      {/* Submissions Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : sortedSubmissions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <List className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No submissions found</h3>
          <p className="text-gray-600 mb-6">
            {submissions.length === 0 
              ? "No candidate submissions have been created yet"
              : "No submissions match your current filters"
            }
          </p>
          {submissions.length > 0 && (
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setCandidateStatusFilter('all');
                setJobStatusFilter('all');
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input 
                      type="checkbox" 
                      className="rounded" 
                      checked={selectedSubmissions.length === filteredAndSortedSubmissions.length && filteredAndSortedSubmissions.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('id')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>ID</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('candidate_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Candidate</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('candidate_status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Candidate Status</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('job_title')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Job</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('job_status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Job Status</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('current_status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Submission Status</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('submission_date')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Date Added</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('recruiter_name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Recruiter</span>
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSubmissions.map((submission, index) => (
                  <tr key={submission.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input 
                        type="checkbox" 
                        className="rounded" 
                        checked={selectedSubmissions.includes(submission.id)}
                        onChange={() => handleSelectSubmission(submission.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {submission.id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {submission.candidate_name || 'Unknown Candidate'}
                          </div>
                          <div className="text-sm text-gray-500">{submission.candidate_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {submission.candidate_status || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.job_title || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {submission.job_status || 'Open'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.current_status)}`}>
                        {submission.current_status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Sourced'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(submission.submission_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {submission.recruiter_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="relative">
                        <button
                          className="text-gray-400 hover:text-gray-600 p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdown = e.currentTarget.nextElementSibling;
                            if (dropdown.classList.contains('hidden')) {
                              // Close all other dropdowns
                              document.querySelectorAll('.submission-action-dropdown').forEach(d => d.classList.add('hidden'));
                              // Show this dropdown
                              dropdown.classList.remove('hidden');
                            } else {
                              dropdown.classList.add('hidden');
                            }
                          }}
                          title="More Actions"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {/* Actions Dropdown Menu */}
                        <div className="submission-action-dropdown hidden absolute z-[100] right-0 mt-1 w-48 bg-white rounded-md shadow-xl border border-gray-200 ring-1 ring-black ring-opacity-5">
                          <div className="py-1">
                            <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </button>
                            <button 
                              onClick={() => handleEditSubmission(submission)}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              title="Edit submission status"
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Submission Status
                            </button>
                            <button 
                              onClick={() => handleChangeStatus(submission)}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <ArrowUpDown className="w-4 h-4 mr-2" />
                              Change Status
                            </button>
                            <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                              <FileText className="w-4 h-4 mr-2" />
                              Add Note
                            </button>
                            <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                              <Mail className="w-4 h-4 mr-2" />
                              Send Message
                            </button>
                            <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                              <Calendar className="w-4 h-4 mr-2" />
                              Schedule Interview
                            </button>
                            <hr className="my-1" />
                            <button className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove Submission
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ‹
              </button>
              <span className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded">
                {currentPage}
              </span>
              <span className="text-sm text-gray-600">of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CandidatePipeline;